import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiArrowLeft, FiSave, FiExternalLink, FiPlus, FiTrash2,
  FiFileText, FiAlertCircle, FiCheckCircle, FiTag, FiClock, FiHash,
} from 'react-icons/fi';
import './views.css';

const EditPageView = ({ slug, onBack, onSaved }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`/api/pages/${slug}`)
      .then((r) => r.json())
      .then((j) => {
        if (!j.success) throw new Error(j.error || 'Page not found');
        setPage(j.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const update = (k, v) => {
    setPage((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const updateSection = (i, field, value) => {
    setPage((p) => {
      const sections = p.sections.map((s, idx) => (idx === i ? { ...s, [field]: value } : s));
      return { ...p, sections };
    });
    setDirty(true);
  };

  const addSection = () => {
    setPage((p) => ({ ...p, sections: [...p.sections, { heading: '', body: '' }] }));
    setDirty(true);
  };

  const removeSection = (i) => {
    setPage((p) => ({ ...p, sections: p.sections.filter((_, idx) => idx !== i) }));
    setDirty(true);
  };

  const updateFaq = (i, field, value) => {
    setPage((p) => {
      const faqs = p.faqs.map((f, idx) => (idx === i ? { ...f, [field]: value } : f));
      return { ...p, faqs };
    });
    setDirty(true);
  };

  const addFaq = () => {
    setPage((p) => ({ ...p, faqs: [...p.faqs, { q: '', a: '' }] }));
    setDirty(true);
  };

  const removeFaq = (i) => {
    setPage((p) => ({ ...p, faqs: p.faqs.filter((_, idx) => idx !== i) }));
    setDirty(true);
  };

  const save = async () => {
    if (!page.h1.trim()) {
      setError('H1 cannot be empty.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/pages/${slug}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          h1: page.h1,
          metaDescription: page.metaDescription,
          intro: page.intro,
          sections: page.sections,
          faqs: page.faqs,
          status: page.status,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Save failed');
      setPage(json.data);
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      if (onSaved) onSaved(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="vw">
        <div className="vw-card vw-empty">
          <span className="vw-spinner vw-spinner-lg" />
          Loading page…
        </div>
      </div>
    );
  }

  if (error && !page) {
    return (
      <div className="vw">
        <div className="vw-card vw-empty">
          <FiAlertCircle className="vw-empty-icon" style={{ color: '#f87171' }} />
          {error}
          <button className="vw-btn vw-btn-ghost vw-btn-sm" onClick={onBack}>
            <FiArrowLeft /> Back to All Pages
          </button>
        </div>
      </div>
    );
  }

  if (!page) return null;

  const wordCountEst =
    estimateWords(page.intro) +
    page.sections.reduce((a, s) => a + estimateWords(s.body), 0) +
    page.faqs.reduce((a, f) => a + estimateWords(f.a), 0);

  return (
    <div className="vw">
      <motion.div className="vw-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="vw-card-head vw-card-head-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="vw-btn vw-btn-ghost vw-btn-sm" onClick={onBack}>
              <FiArrowLeft /> Back
            </button>
            <div>
              <h2>Edit Page</h2>
              <p>
                <code style={{ color: '#c4b5fd' }}>/blog/{page.slug}</code>
                {' · '}
                <span style={{ color: '#8089a3' }}>{page.categoryLabel}</span>
              </p>
            </div>
          </div>
          <div className="vw-head-actions">
            <a
              href={`/blog/${page.slug}`}
              target="_blank"
              rel="noreferrer"
              className="vw-btn vw-btn-ghost vw-btn-sm"
            >
              <FiExternalLink /> View
            </a>
            <button
              className="vw-btn vw-btn-primary vw-btn-sm"
              onClick={save}
              disabled={saving || !dirty}
            >
              {saving ? <><span className="vw-spinner" /> Saving…</> : <><FiSave /> {dirty ? 'Save Changes' : 'Saved'}</>}
            </button>
          </div>
        </div>

        {savedFlash && (
          <div className="vw-edit-flash">
            <FiCheckCircle /> Saved successfully. JSON-LD, word count and sitemap updated.
          </div>
        )}

        {error && (
          <div className="vw-error vw-error-block">
            <FiAlertCircle /> {error}
          </div>
        )}

        {/* Quick stats row */}
        <div className="vw-edit-stats">
          <div className="vw-stat-pill"><FiFileText /> <strong>{wordCountEst.toLocaleString()}</strong> words</div>
          <div className="vw-stat-pill"><FiHash /> <strong>{page.sections.length}</strong> sections</div>
          <div className="vw-stat-pill"><FiHash /> <strong>{page.faqs.length}</strong> FAQs</div>
          <div className="vw-stat-pill"><FiClock /> Updated {new Date(page.updatedAt).toLocaleString()}</div>
          <div className="vw-stat-pill"><FiTag /> {page.intent || page.category}</div>
        </div>

        {/* Top fields */}
        <div className="vw-edit-grid">
          <label className="vw-field">
            <span className="vw-label">Status</span>
            <select
              className="vw-toolbar-select"
              value={page.status}
              onChange={(e) => update('status', e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </label>

          <label className="vw-field vw-field-grow">
            <span className="vw-label">H1 Heading</span>
            <div className="vw-select-wrap">
              <FiHash className="vw-field-icon" />
              <input
                type="text"
                value={page.h1}
                onChange={(e) => update('h1', e.target.value)}
                placeholder="Page H1"
              />
            </div>
          </label>
        </div>

        <label className="vw-field" style={{ marginBottom: 16 }}>
          <span className="vw-label">Meta Description</span>
          <textarea
            className="vw-edit-textarea"
            rows={3}
            value={page.metaDescription}
            onChange={(e) => update('metaDescription', e.target.value)}
            placeholder="160-character SEO description"
          />
          <span className="vw-edit-counter">
            {page.metaDescription.length} chars · ideal 140–160
          </span>
        </label>

        <label className="vw-field" style={{ marginBottom: 24 }}>
          <span className="vw-label">Intro Paragraph</span>
          <textarea
            className="vw-edit-textarea"
            rows={6}
            value={page.intro}
            onChange={(e) => update('intro', e.target.value)}
            placeholder="Opening paragraph shown below H1"
          />
          <span className="vw-edit-counter">{estimateWords(page.intro)} words</span>
        </label>

        {/* Sections */}
        <div className="vw-edit-section-head">
          <h3>Content Sections</h3>
          <button className="vw-btn vw-btn-ghost vw-btn-sm" onClick={addSection}>
            <FiPlus /> Add Section
          </button>
        </div>

        <div className="vw-edit-list">
          {page.sections.map((s, i) => (
            <div key={i} className="vw-edit-item">
              <div className="vw-edit-item-head">
                <span className="vw-edit-item-label">Section {i + 1}</span>
                <button
                  className="vw-icon-btn vw-danger"
                  onClick={() => removeSection(i)}
                  title="Remove section"
                  type="button"
                >
                  <FiTrash2 />
                </button>
              </div>
              <input
                type="text"
                className="vw-edit-input"
                value={s.heading}
                onChange={(e) => updateSection(i, 'heading', e.target.value)}
                placeholder="Section heading (H2)"
              />
              <textarea
                className="vw-edit-textarea"
                rows={5}
                value={s.body}
                onChange={(e) => updateSection(i, 'body', e.target.value)}
                placeholder="Section body…"
              />
              <span className="vw-edit-counter">{estimateWords(s.body)} words</span>
            </div>
          ))}
          {page.sections.length === 0 && (
            <div className="vw-empty" style={{ padding: '24px 16px' }}>
              No sections yet. Click <strong>Add Section</strong> to create one.
            </div>
          )}
        </div>

        {/* FAQs */}
        <div className="vw-edit-section-head" style={{ marginTop: 24 }}>
          <h3>FAQ Schema</h3>
          <button className="vw-btn vw-btn-ghost vw-btn-sm" onClick={addFaq}>
            <FiPlus /> Add FAQ
          </button>
        </div>

        <div className="vw-edit-list">
          {page.faqs.map((f, i) => (
            <div key={i} className="vw-edit-item">
              <div className="vw-edit-item-head">
                <span className="vw-edit-item-label">FAQ {i + 1}</span>
                <button
                  className="vw-icon-btn vw-danger"
                  onClick={() => removeFaq(i)}
                  title="Remove FAQ"
                  type="button"
                >
                  <FiTrash2 />
                </button>
              </div>
              <input
                type="text"
                className="vw-edit-input"
                value={f.q}
                onChange={(e) => updateFaq(i, 'q', e.target.value)}
                placeholder="Question…"
              />
              <textarea
                className="vw-edit-textarea"
                rows={3}
                value={f.a}
                onChange={(e) => updateFaq(i, 'a', e.target.value)}
                placeholder="Answer…"
              />
            </div>
          ))}
          {page.faqs.length === 0 && (
            <div className="vw-empty" style={{ padding: '24px 16px' }}>
              No FAQs yet. Add at least 4 for richer schema coverage.
            </div>
          )}
        </div>

        {/* Footer save bar */}
        <div className="vw-edit-footer">
          <span className="vw-muted">
            {dirty ? 'Unsaved changes' : 'All changes saved'}
            {' · '}
            JSON-LD and sitemap regenerate on save
          </span>
          <button
            className="vw-btn vw-btn-primary"
            style={{ flex: 'none', padding: '12px 24px' }}
            onClick={save}
            disabled={saving || !dirty}
          >
            {saving ? <><span className="vw-spinner" /> Saving…</> : <><FiSave /> Save Changes</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

function estimateWords(str) {
  if (!str) return 0;
  return String(str).trim().split(/\s+/).filter(Boolean).length;
}

export default EditPageView;
